/**
 * AdminMemberDetailPage - 회원 상세 조회 및 편집 페이지
 *
 * /centers/:centerId/members/:memberId 경로로 접근하며,
 * 선택한 회원의 상세 정보를 표시하고 강사별 재등록 관계 및 정산 위탁대금을 편집할 수 있다.
 */

import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useCurrentUser } from "@/components/AuthProvider";
import { useCenterContext } from "@/contexts/CenterContext";
import { PurchaseTicketModal } from "@/components/PurchaseTicketModal";
import { EditMemberModal } from "@/components/EditMemberModal";
import { EditMemberTicketModal } from "@/components/EditMemberTicketModal";
import { useDeleteMember } from "@/hooks/useMembers";
import {
  getMemberTickets,
  useExpireMemberTicket,
  useDeleteMemberTicket,
  type MemberTicket,
} from "@/hooks/useTicketPurchase";
import { getDocs, collection } from "firebase/firestore";
import { getFirebaseFirestore } from "@/config/firebase";
import {
  getAdminMember,
  getMemberReRegistrations,
  setMemberInstructorReRegistration,
  clearMemberInstructorReRegistration,
  updateMemberSettlementUnitPrice,
  type Member,
  type ReRegistrationEntry,
} from "@/services/firestore/admin-members";

interface InstructorInfo {
  id: string;
  name: string;
}

export function AdminMemberDetailPage() {
  const { centerId, memberId } = useParams<{
    centerId: string;
    memberId: string;
  }>();
  const user = useCurrentUser();
  const navigate = useNavigate();
  const { currentCenter } = useCenterContext();

  // --- 회원 데이터 상태 ---
  const [member, setMember] = useState<Member | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  /** 수정 후 상세 데이터를 다시 불러오기 위한 nonce */
  const [reloadNonce, setReloadNonce] = useState(0);

  // --- 수정/삭제 상태 ---
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const deleteMember = useDeleteMember();

  // --- top-level members/{memberId}.tickets[] (수강권 보유 목록) ---
  const [tickets, setTickets] = useState<MemberTicket[]>([]);
  const [isPurchaseOpen, setIsPurchaseOpen] = useState(false);
  const [ticketActionError, setTicketActionError] = useState<string | null>(null);
  const expireMemberTicket = useExpireMemberTicket();
  const deleteMemberTicket = useDeleteMemberTicket();
  /** 수정 모달 대상: 직렬화 순번과 함께 보관(가드용) */
  const [editingTicket, setEditingTicket] = useState<{
    ticket: MemberTicket;
    index: number;
  } | null>(null);

  // --- 수강권 목록 refetch ---
  const refetchTickets = useCallback(async () => {
    if (!centerId || !memberId) return;
    const parsedId = Number(memberId);
    if (!Number.isInteger(parsedId) || parsedId < 1) return;
    const items = await getMemberTickets(Number(centerId), parsedId);
    setTickets(items);
  }, [centerId, memberId]);

  // --- 수강권 만료(소프트 비활성) ---
  const handleExpireTicket = async (ticket: MemberTicket, ticketIndex: number) => {
    if (!centerId || !memberId) return;
    const parsedId = Number(memberId);
    if (!Number.isInteger(parsedId) || parsedId < 1) return;

    const confirmed = window.confirm(
      `수강권 "${ticket.title}"을(를) 만료 처리하시겠습니까?\n만료 후에는 비활성 처리되며 예약에 사용할 수 없습니다. (이력은 보존됩니다)`,
    );
    if (!confirmed) return;

    setTicketActionError(null);
    try {
      await expireMemberTicket.mutateAsync({
        centerId: Number(centerId),
        memberId: parsedId,
        ticketIndex,
        expectedTitle: ticket.title,
        expectedPurchasedAt: ticket.purchasedAt,
      });
      await refetchTickets();
    } catch (err: unknown) {
      setTicketActionError(err instanceof Error ? err.message : "수강권 만료에 실패했습니다.");
    }
  };

  // --- 수강권 물리 삭제(하드) ---
  const handleDeleteTicket = async (ticket: MemberTicket, ticketIndex: number) => {
    if (!centerId || !memberId) return;
    const parsedId = Number(memberId);
    if (!Number.isInteger(parsedId) || parsedId < 1) return;

    const confirmed = window.confirm(
      `수강권 "${ticket.title}"을(를) 완전히 삭제하시겠습니까?\n삭제하면 목록에서 제거되며 구매·사용 이력이 보존되지 않습니다. 되돌릴 수 없습니다.`,
    );
    if (!confirmed) return;

    setTicketActionError(null);
    try {
      await deleteMemberTicket.mutateAsync({
        centerId: Number(centerId),
        memberId: parsedId,
        ticketIndex,
        expectedTitle: ticket.title,
        expectedPurchasedAt: ticket.purchasedAt,
      });
      await refetchTickets();
    } catch (err: unknown) {
      setTicketActionError(err instanceof Error ? err.message : "수강권 삭제에 실패했습니다.");
    }
  };

  // --- N:N 재등록 관계 상태 ---
  const [reRegistrations, setReRegistrations] = useState<ReRegistrationEntry[]>([]);
  const [instructors, setInstructors] = useState<InstructorInfo[]>([]);
  const [selectedInstructorId, setSelectedInstructorId] = useState("");
  const [newReRegDate, setNewReRegDate] = useState("");
  const [isSavingReReg, setIsSavingReReg] = useState(false);
  const [reRegSaveSuccess, setReRegSaveSuccess] = useState(false);
  const [reRegSaveError, setReRegSaveError] = useState<string | null>(null);

  // --- 정산 기본 위탁대금 편집 상태 ---
  const [settlementUnitPrice, setSettlementUnitPrice] = useState("");
  const [isSavingPrice, setIsSavingPrice] = useState(false);
  const [priceSaveSuccess, setPriceSaveSuccess] = useState(false);
  const [priceSaveError, setPriceSaveError] = useState<string | null>(null);

  // --- 재등록 관계 조회 ---
  const fetchReRegistrations = useCallback(async () => {
    if (!centerId || !memberId) return;
    const entries = await getMemberReRegistrations(user.uid, Number(centerId), memberId);
    setReRegistrations(entries);
  }, [user.uid, centerId, memberId]);

  // --- 회원 데이터 + 강사 목록 + 재등록 관계 로드 ---
  useEffect(() => {
    let cancelled = false;

    async function fetchData() {
      if (!centerId || !memberId) return;

      setIsLoading(true);
      setError(null);

      try {
        const parsedMemberId = Number(memberId);
        const validTopLevelId = Number.isInteger(parsedMemberId) && parsedMemberId >= 1;
        const [result, reRegs, instructorsSnap, topLevelTickets] = await Promise.all([
          getAdminMember(user.uid, Number(centerId), memberId),
          getMemberReRegistrations(user.uid, Number(centerId), memberId),
          getDocs(
            collection(
              getFirebaseFirestore(),
              "users",
              user.uid,
              "centers",
              centerId,
              "instructors",
            ),
          ),
          validTopLevelId
            ? getMemberTickets(Number(centerId), parsedMemberId)
            : Promise.resolve([]),
        ]);

        if (!cancelled) {
          setMember(result);
          setReRegistrations(reRegs);
          setTickets(topLevelTickets);
          setInstructors(
            instructorsSnap.docs.map((d) => ({
              id: d.id,
              name: (d.data() as { name?: string }).name ?? "알 수 없는 강사",
            })),
          );
          if (result) {
            setSettlementUnitPrice(
              result.settlementUnitPrice !== undefined ? String(result.settlementUnitPrice) : "",
            );
          }
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error("회원 데이터 조회 실패"));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    void fetchData();

    return () => {
      cancelled = true;
    };
  }, [user.uid, centerId, memberId, reloadNonce]);

  // --- 회원 삭제(소프트) ---
  const handleDeleteMember = async () => {
    if (!centerId || !memberId || !member) return;
    const parsedMemberId = Number(memberId);
    if (!Number.isInteger(parsedMemberId) || parsedMemberId < 1) return;

    const confirmed = window.confirm(
      `회원 "${member.name}"을(를) 삭제하시겠습니까?\n과거 예약·정산 기록은 보존되며, 목록과 신규 예약에서 제외됩니다.`,
    );
    if (!confirmed) return;

    setDeleteError(null);
    try {
      await deleteMember.mutateAsync({ centerId: Number(centerId), memberId: parsedMemberId });
      navigate(`/centers/${centerId}/members`);
    } catch (err: unknown) {
      setDeleteError(err instanceof Error ? err.message : "회원 삭제에 실패했습니다.");
    }
  };

  // --- 재등록 관계 추가 ---
  const handleAddReRegistration = async () => {
    if (!centerId || !memberId || !selectedInstructorId || !newReRegDate) return;

    setIsSavingReReg(true);
    setReRegSaveSuccess(false);
    setReRegSaveError(null);

    try {
      await setMemberInstructorReRegistration(
        user.uid,
        Number(centerId),
        memberId,
        selectedInstructorId,
        newReRegDate,
      );
      await fetchReRegistrations();
      setSelectedInstructorId("");
      setNewReRegDate("");
      setReRegSaveSuccess(true);
    } catch (err: unknown) {
      setReRegSaveError(err instanceof Error ? err.message : "저장에 실패했습니다.");
    } finally {
      setIsSavingReReg(false);
    }
  };

  // --- 재등록 관계 삭제 ---
  const handleDeleteReRegistration = async (docId: string) => {
    if (!centerId) return;

    setIsSavingReReg(true);
    setReRegSaveError(null);

    try {
      await clearMemberInstructorReRegistration(user.uid, Number(centerId), docId);
      await fetchReRegistrations();
    } catch (err: unknown) {
      setReRegSaveError(err instanceof Error ? err.message : "삭제에 실패했습니다.");
    } finally {
      setIsSavingReReg(false);
    }
  };

  // --- 정산 기본 위탁대금 저장 ---
  const handleSaveSettlementUnitPrice = async () => {
    if (!centerId || !memberId) return;

    const parsed = Number(settlementUnitPrice);
    if (isNaN(parsed) || parsed < 0) {
      setPriceSaveError("0 이상의 숫자를 입력해주세요.");
      return;
    }

    setIsSavingPrice(true);
    setPriceSaveSuccess(false);
    setPriceSaveError(null);

    try {
      await updateMemberSettlementUnitPrice(user.uid, Number(centerId), memberId, parsed);
      setPriceSaveSuccess(true);
    } catch (err: unknown) {
      setPriceSaveError(err instanceof Error ? err.message : "저장에 실패했습니다.");
    } finally {
      setIsSavingPrice(false);
    }
  };

  // --- 정산 기본 위탁대금 초기화 ---
  const handleClearSettlementUnitPrice = async () => {
    if (!centerId || !memberId) return;

    setIsSavingPrice(true);
    setPriceSaveSuccess(false);
    setPriceSaveError(null);

    try {
      await updateMemberSettlementUnitPrice(user.uid, Number(centerId), memberId, null);
      setSettlementUnitPrice("");
      setPriceSaveSuccess(true);
    } catch (err: unknown) {
      setPriceSaveError(err instanceof Error ? err.message : "초기화에 실패했습니다.");
    } finally {
      setIsSavingPrice(false);
    }
  };

  // --- 강사 이름 조회 헬퍼 ---
  const getInstructorName = (instructorId: string) => {
    const instructor = instructors.find((i) => i.id === instructorId);
    return instructor?.name ?? "알 수 없는 강사";
  };

  // --- 이미 재등록 설정된 강사 제외 ---
  const availableInstructors = instructors.filter(
    (i) => !reRegistrations.some((r) => r.instructorId === i.id),
  );

  // --- 수강권 구매 모달용 centerName: URL의 centerId와 컨텍스트의 currentCenter가
  //     일치할 때만 노출. 불일치하면 prefill이 부정확해질 수 있으므로 버튼 자체를 숨긴다.
  const centerName =
    currentCenter && centerId && currentCenter.centerId === Number(centerId)
      ? currentCenter.name
      : null;

  const backLink = `/centers/${centerId}/members`;

  // --- 로딩 상태 ---
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Link to={backLink} className="text-blue-600 hover:text-blue-800 text-sm">
            &larr; 회원 목록
          </Link>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </div>
    );
  }

  // --- 에러 상태 ---
  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <Link to={backLink} className="text-blue-600 hover:text-blue-800 text-sm">
            &larr; 회원 목록
          </Link>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-red-700">오류 발생</h2>
          <p className="text-red-600 mt-1">{error.message}</p>
        </div>
      </div>
    );
  }

  // --- 회원을 찾을 수 없는 상태 ---
  if (!member) {
    return (
      <div className="space-y-6">
        <div>
          <Link to={backLink} className="text-blue-600 hover:text-blue-800 text-sm">
            &larr; 회원 목록
          </Link>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <h2 className="text-lg font-semibold text-yellow-700">회원을 찾을 수 없습니다</h2>
          <p className="text-yellow-600 mt-1">요청하신 회원이 존재하지 않거나 삭제되었습니다.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 뒤로가기 */}
      <div>
        <Link to={backLink} className="text-blue-600 hover:text-blue-800 text-sm">
          &larr; 회원 목록
        </Link>
      </div>

      {/* 헤더: 회원 기본 정보 */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-start justify-between gap-4">
          <h1 className="text-2xl font-bold text-gray-900">{member.name}</h1>
          <div className="flex items-center gap-2">
            {member.contact && centerName && (
              <button
                aria-label="수강권 구매"
                onClick={() => setIsPurchaseOpen(true)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                수강권 구매
              </button>
            )}
            <button
              aria-label="회원 정보 수정"
              onClick={() => setIsEditOpen(true)}
              className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
            >
              수정
            </button>
            <button
              aria-label="회원 삭제"
              onClick={() => void handleDeleteMember()}
              disabled={deleteMember.isPending}
              className="px-4 py-2 text-sm font-medium text-red-600 border border-red-300 rounded-md hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {deleteMember.isPending ? "삭제 중..." : "삭제"}
            </button>
          </div>
        </div>
        {deleteError && <p className="mt-3 text-sm text-red-600">{deleteError}</p>}
        <div className="mt-3 space-y-1 text-sm text-gray-600">
          {member.contact && (
            <p>
              <span className="font-medium text-gray-500">연락처:</span> {member.contact}
            </p>
          )}
          <p>
            <span className="font-medium text-gray-500">회원 유형:</span>{" "}
            <span className="font-semibold text-gray-800">
              {reRegistrations.length > 0 ? `재등록 (${reRegistrations.length}명 강사)` : "신규"}
            </span>
          </p>
        </div>
      </div>

      {/* 보유 수강권 — top-level members.tickets[] */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-800">보유 수강권</h2>
        {ticketActionError && <p className="mt-2 text-sm text-red-600">{ticketActionError}</p>}
        {tickets.length === 0 ? (
          <p className="text-sm text-gray-500 mt-2">아직 구매한 수강권이 없습니다.</p>
        ) : (
          <table className="w-full text-sm mt-3">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 font-medium text-gray-600">수강권</th>
                <th className="text-left py-2 font-medium text-gray-600">유형</th>
                <th className="text-right py-2 font-medium text-gray-600">잔여/총</th>
                <th className="text-left py-2 font-medium text-gray-600">만료일</th>
                <th className="text-left py-2 font-medium text-gray-600">구매일</th>
                <th className="text-left py-2 font-medium text-gray-600">상태</th>
                <th className="text-right py-2 font-medium text-gray-600">관리</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((t, idx) => (
                <tr
                  key={`${t.title}-${t.purchasedAt ?? "n/a"}-${idx}`}
                  className="border-b border-gray-100"
                >
                  <td className="py-2 text-gray-800">{t.title}</td>
                  <td className="py-2 text-gray-800">{t.type}</td>
                  <td className="py-2 text-right text-gray-800">
                    {t.remainingCoupon} / {t.maxCoupon}
                  </td>
                  <td className="py-2 text-gray-800">
                    {t.expireAt ? new Date(t.expireAt).toLocaleDateString("ko-KR") : "무제한"}
                  </td>
                  <td className="py-2 text-gray-800">
                    {t.purchasedAt ? new Date(t.purchasedAt).toLocaleDateString("ko-KR") : "-"}
                  </td>
                  <td className="py-2 text-gray-800">{t.isActive ? "활성" : "비활성"}</td>
                  <td className="py-2 text-right">
                    <div className="flex items-center justify-end gap-3">
                      {t.isActive && (
                        <>
                          <button
                            aria-label={`${t.title} 수정`}
                            onClick={() => setEditingTicket({ ticket: t, index: idx })}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            수정
                          </button>
                          <button
                            aria-label={`${t.title} 만료`}
                            onClick={() => void handleExpireTicket(t, idx)}
                            disabled={expireMemberTicket.isPending}
                            className="text-amber-600 hover:text-amber-800 disabled:opacity-50"
                          >
                            만료
                          </button>
                        </>
                      )}
                      <button
                        aria-label={`${t.title} 삭제`}
                        onClick={() => void handleDeleteTicket(t, idx)}
                        disabled={deleteMemberTicket.isPending}
                        className="text-red-600 hover:text-red-800 disabled:opacity-50"
                      >
                        삭제
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 강사별 재등록 관계 관리 */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-800">강사별 재등록 설정</h2>
        <p className="text-sm text-gray-500 mt-1">
          강사별로 재등록 시기를 개별 설정합니다. 설정된 날짜부터 해당 강사의 수업은 재등록
          위탁대금으로 정산됩니다.
        </p>

        {/* 현재 재등록 관계 목록 */}
        {reRegistrations.length > 0 && (
          <div className="mt-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 font-medium text-gray-600">강사</th>
                  <th className="text-left py-2 font-medium text-gray-600">재등록 시기</th>
                  <th className="text-right py-2 font-medium text-gray-600">관리</th>
                </tr>
              </thead>
              <tbody>
                {reRegistrations.map((entry) => (
                  <tr key={entry.docId} className="border-b border-gray-100">
                    <td className="py-2 text-gray-800">{getInstructorName(entry.instructorId)}</td>
                    <td className="py-2 text-gray-800">{entry.reRegistrationDate}</td>
                    <td className="py-2 text-right">
                      <button
                        aria-label={`${getInstructorName(entry.instructorId)} 재등록 삭제`}
                        onClick={() => void handleDeleteReRegistration(entry.docId)}
                        disabled={isSavingReReg}
                        className="text-red-600 hover:text-red-800 text-sm disabled:opacity-50"
                      >
                        삭제
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* 재등록 관계 추가 폼 */}
        <div className="mt-4 space-y-3">
          <div className="flex items-center gap-3">
            <select
              aria-label="강사 선택"
              value={selectedInstructorId}
              onChange={(e) => {
                setSelectedInstructorId(e.target.value);
                setReRegSaveSuccess(false);
              }}
              className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">강사 선택</option>
              {availableInstructors.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name}
                </option>
              ))}
            </select>
            <input
              aria-label="재등록 시기"
              type="date"
              value={newReRegDate}
              onChange={(e) => {
                setNewReRegDate(e.target.value);
                setReRegSaveSuccess(false);
              }}
              className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <button
              aria-label="재등록 추가"
              onClick={() => void handleAddReRegistration()}
              disabled={isSavingReReg || !selectedInstructorId || !newReRegDate}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSavingReReg ? "저장 중..." : "추가"}
            </button>
          </div>

          {reRegSaveSuccess && <p className="text-sm text-green-600">저장되었습니다.</p>}
          {reRegSaveError && <p className="text-sm text-red-600">{reRegSaveError}</p>}
        </div>
      </div>

      {/* 정산 기본 위탁대금 편집 */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-800">정산 기본 위탁대금</h2>
        <p className="text-sm text-gray-500 mt-1">
          회원별 개별 위탁대금을 설정하면 센터 공통 위탁대금(payScale) 대신 적용됩니다. 초기화하면
          센터 공통 위탁대금으로 복귀합니다.
        </p>

        <div className="mt-4 space-y-3">
          <div className="flex items-center gap-4">
            <label
              htmlFor="settlementUnitPrice"
              className="text-sm font-medium text-gray-700 sr-only"
            >
              정산 기본 위탁대금
            </label>
            <input
              id="settlementUnitPrice"
              aria-label="정산 기본 위탁대금"
              type="number"
              min="0"
              step="1000"
              placeholder="미설정 (센터 공통 위탁대금 적용)"
              value={settlementUnitPrice}
              onChange={(e) => {
                setSettlementUnitPrice(e.target.value);
                setPriceSaveSuccess(false);
              }}
              className="w-40 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-600">원</span>
            <button
              aria-label="정산 기본 위탁대금 저장"
              onClick={() => void handleSaveSettlementUnitPrice()}
              disabled={isSavingPrice || settlementUnitPrice === ""}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSavingPrice ? "저장 중..." : "저장"}
            </button>
            {settlementUnitPrice !== "" && (
              <button
                aria-label="정산 기본 위탁대금 초기화"
                onClick={() => void handleClearSettlementUnitPrice()}
                disabled={isSavingPrice}
                className="px-4 py-2 text-sm font-medium text-red-600 border border-red-300 rounded-md hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                초기화
              </button>
            )}
          </div>

          {priceSaveSuccess && <p className="text-sm text-green-600">저장되었습니다.</p>}
          {priceSaveError && <p className="text-sm text-red-600">{priceSaveError}</p>}
        </div>
      </div>

      {/* 회원 정보 수정 모달 */}
      {centerId && (
        <EditMemberModal
          isOpen={isEditOpen}
          centerId={Number(centerId)}
          memberId={Number(memberId)}
          initialName={member.name}
          initialMobile={member.contact ?? ""}
          onClose={() => setIsEditOpen(false)}
          onUpdated={() => {
            setIsEditOpen(false);
            setReloadNonce((n) => n + 1);
          }}
        />
      )}

      {/* 수강권 수정 모달 */}
      {editingTicket && centerId && memberId && (
        <EditMemberTicketModal
          isOpen={true}
          centerId={Number(centerId)}
          memberId={Number(memberId)}
          ticket={editingTicket.ticket}
          ticketIndex={editingTicket.index}
          onClose={() => setEditingTicket(null)}
          onUpdated={() => {
            setEditingTicket(null);
            void refetchTickets();
          }}
        />
      )}

      {/* 수강권 구매 모달 */}
      {centerName && member.contact && centerId && (
        <PurchaseTicketModal
          isOpen={isPurchaseOpen}
          ownerUid={user.uid}
          centerId={Number(centerId)}
          centerName={centerName}
          memberName={member.name}
          memberMobile={member.contact}
          onClose={() => setIsPurchaseOpen(false)}
          onCreated={() => {
            setIsPurchaseOpen(false);
            void refetchTickets();
          }}
        />
      )}
    </div>
  );
}
